import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';

/** Обновления фронта (Алексей 20.10, AI294 → #1639).
 *  Фронт — PWA: новый билд = новый sw.js (skipWaiting + clientsClaim), браузер
 *  проверяет его раз в минуту (registration.update). Когда новый воркер взял страницу
 *  под контроль (controllerchange), на странице всё ещё старый код:
 *  - на дашборде НЕ перезагружаем сами — Chrome при перезагрузке выходит из
 *    полноэкранного, адресная строка возвращается (#1637). Вместо этого панель
 *    дашборда показывает кнопку «Пришли обновления — обновите страницу»;
 *  - на остальных страницах — как раньше, тихая перезагрузка, когда безопасно
 *    (не посреди диалога, вкладка видима).
 *  После перезагрузки по кнопке дашборд показывает «Обновлено успешно» и большую
 *  кнопку «Развернуть на весь экран» (флаг в sessionStorage).
 *  Установленная PWA (03.11 #8585) — без кнопки: дашборд перезагружается сам, как остальные. */
const CHECK_INTERVAL_MS = 60_000;
const RETRY_INTERVAL_MS = 15_000;
const JUST_UPDATED_KEY = 'app:justUpdated';
const DASHBOARD_PATH = '/usage/dashboard';

let updateReady = false;
const listeners = new Set<() => void>();
function setUpdateReady() {
  if (updateReady) return;
  updateReady = true;
  listeners.forEach((l) => l());
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

/** Открыто как установленная PWA (с экрана «Домой»): адресной строки нет, и перезагрузка
 *  не выбрасывает из полноэкранного — в отличие от вкладки браузера (#1637). */
export function isInstalledPwa(): boolean {
  try {
    if ((navigator as Navigator & { standalone?: boolean }).standalone) return true;
    return ['fullscreen', 'standalone', 'minimal-ui'].some((m) => window.matchMedia(`(display-mode: ${m})`).matches);
  } catch {
    return false;
  }
}

function useUpdateLoaded(): boolean {
  return useSyncExternalStore(subscribe, () => updateReady, () => false);
}

/** Показать ли кнопку «Пришли обновления — обновите страницу». В установленной PWA — нет:
 *  там дашборд перезагружается сам (Алексей 03.11 #8585 «без кнопки ручного обновления»). */
export function useUpdateReady(): boolean {
  return useUpdateLoaded() && !isInstalledPwa();
}

/** Применить обновление: перезагрузка с пометкой «это по обновлению». */
export function applyUpdate() {
  try { sessionStorage.setItem(JUST_UPDATED_KEY, '1'); } catch { /* приватный режим */ }
  window.location.reload();
}

/** Одноразово: страница загружена по кнопке «обновите страницу»? Флаг снимается. */
export function consumeJustUpdated(): boolean {
  try {
    if (sessionStorage.getItem(JUST_UPDATED_KEY) !== '1') return false;
    sessionStorage.removeItem(JUST_UPDATED_KEY);
    return true;
  } catch {
    return false;
  }
}

function safeToReload(): boolean {
  return document.visibilityState === 'visible' && !document.querySelector('[role="dialog"]');
}

export function useAutoReloadOnUpdate() {
  const { pathname } = useLocation();
  const ready = useUpdateLoaded();

  // Регистрация воркера — сами (#1899): клиент плагина перезагружал страницу на
  // смене воркера. Здесь только register + проверка раз в минуту; что делать с новым
  // воркером, решает controllerchange ниже (дашборд — кнопка, остальные — тихо).
  useEffect(() => {
    if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
    let timer = 0;
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).then((registration) => {
      timer = window.setInterval(() => {
        if (!navigator.onLine) return;
        registration.update().catch(() => { /* офлайн / сеть — попробуем через минуту */ });
      }, CHECK_INTERVAL_MS);
    }).catch(() => { /* без воркера приложение работает как обычный сайт */ });
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    // Первый controllerchange на свежей странице — установка воркера, а не обновление.
    const hadController = !!navigator.serviceWorker.controller;
    const onControllerChange = () => { if (hadController) setUpdateReady(); };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }, []);

  // Не дашборд → тихо перезагружаемся, как только безопасно. Дашборд в установленной PWA —
  // тоже тихо: полноэкранный там не теряется, а режим «на весь экран» восстанавливает KioskContext.
  const manualOnly = pathname.startsWith(DASHBOARD_PATH) && !isInstalledPwa();
  useEffect(() => {
    if (!ready || manualOnly) return;
    const reload = () => window.location.reload();
    if (safeToReload()) { reload(); return; }
    const retry = window.setInterval(() => { if (safeToReload()) reload(); }, RETRY_INTERVAL_MS);
    return () => window.clearInterval(retry);
  }, [ready, manualOnly]);
}
